import { AfterViewInit, Component, forwardRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { CustomElement } from '../../classes';
import { ValidationService } from '../../../form/services/validation.service';
import { ApiCollection } from '../../../../api/collection';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';
import { SettingService } from '../../../../settings/services/setting.service';
import { CurrencyService } from '../../../../currency/services/currency.service';
import { CurrencyModel } from '../../../../currency/models/currency.model';
import numeral from 'numeral-es6';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputComponent),
    multi: true
};

@Component({
    selector: 'chassis-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class InputComponent extends CustomElement implements OnInit, AfterViewInit {

    @ViewChild('inputRef') inputElement;

    /**
     * Autocomplete attribute.
     */
    @Input() autocomplete: boolean | string;

    /**
     * Autofocus attribute.
     */
    @Input() autofocus: boolean;

    /**
     * Checked attribute.
     */
    @Input() checked: boolean;

    /**
     * Currency attribute.
     * The input needs to be a number input in order to use currency.
     */
    @Input() currency: string;

    protected locale: string;

    /**
     * The list of registered currencies.
     */
    public currencies: ApiCollection = new ApiCollection;

    /**
     * Minlength attribute.
     */
    @Input() minlength: number = null;

    /**
     * Min attribute.
     */
    @Input() min: number = null;

    /**
     * Maxlength attribute.
     */
    @Input() maxlength: number = null;

    /**
     * Max attribute.
     */
    @Input() max: number = null;

    /**
     * Multiple attribute.
     */
    @Input() multiple: boolean = null;

    /**
     * Placeholder attribute.
     */
    @Input() placeholder: string = null;

    /**
     * Readonly attribute.
     */
    @Input() readonly: boolean;

    @Input('class') classes = '';

    /**
     * Required validation.
     */
    @Input() required: boolean;

    /**
     * Size attribute.
     */
    @Input() size: number = null;

    /**
     * Value attribute.
     */
    @Input() value: string = null;

    /**
     * Loader attribute.
     * If the input is a button, you could show a loader when loading.
     */
    @Input() loader: boolean = null;

    /**
     * Date attributes.
     */
    @Input() format: string = null;

    /**
     * Decimals attribute.
     */
    @Input() decimals = 2;

    /**
     * Whether to apply the number mask (0000 -> 0.000) or not
     *
     * @type {boolean}
     * @default true
     */
    @Input() applyNumberMask: boolean;

    /**
     * Has the date or time picker modal to be shown.
     */
    protected picker = false;

    /**
     * Settings holder.
     */
    protected settings: {
        locale?: string,
        number?: {
            prefix?: string,
            allowDecimal?: boolean,
            decimalLimit?: number,
            decimalSymbol?: string,
            thousandsSeparatorSymbol?: string,
        }
    } = {};

    /**
     * The input masks.
     */
    public masks: {
        number?: Object,
    } = {};

    /**
     * A transformed value holder for inputs using a mask.
     *
     * @type {string}
     */
    public transformedValue = '';

    /**
     * The SettingService instance.
     */
    protected settingService: SettingService;

    /**
     * The ValidationService instance.
     */
    protected validationService: ValidationService;

    /**
     * The CurrencyService instance.
     */
    protected currencyService: CurrencyService;

    /**
     * Constructor.
     *
     * @param injector
     */
    constructor(protected injector: Injector) {
        super();

        this.validationService = this.injector.get(ValidationService);
        this.settingService    = this.injector.get(SettingService);
        this.currencyService   = this.injector.get(CurrencyService);

        this.getAndSetCurrencies();

        this.change.subscribe((value) => {
            if (this.isType(['number', 'currency'])) {
                this.transformedValue = this.toReadableNumberValue(value);
            }
        });
    }

    public ngOnInit() {
        super.ngOnInit();

        if (this.isType(['number', 'currency'])) {
            this.getAndSetBaseCurrency();
            this.getAndSetBaseLocale();
        }

        this.setSettings();

        this.initialValue.subscribe((value: string) => {
            if (this.isType(['number', 'currency'])) {
                this.setInitialNumberValues(value);
            }
        });
    }

    public ngAfterViewInit() {
        if (this.isType(['slug'])) {
            this.setSlugOnBlur();
        }
    }

    /**
     * Execute input mutators on init.
     */
    protected executeMutators() {
        super.executeMutators();

        this.type            = this.currency ? 'currency' : this.type;
        this.autocomplete    = this.autocomplete === false ? 'off' : 'on';
        this.disabled        = this.disabled ? this.disabled : null;
        this.applyNumberMask = this.applyNumberMask !== undefined ? this.applyNumberMask : true;
    }

    /**
     * Get the registered currencies and make them available for input elements to use.
     */
    protected getAndSetCurrencies() {
        this.currencyService.all().subscribe((currencies: ApiCollection) => {
            this.currencies = currencies;
        });
    }

    /**
     * Get the registered base currency and make it available for input elements to use.
     */
    protected getAndSetBaseCurrency() {
        this.settingService.get('general.currency.currency', (value: string) => {
            this.currency = value;
        }, 'EUR');
    }

    /**
     * Get the registered base locale and make it available for input elements to use.
     */
    protected getAndSetBaseLocale() {
        this.settingService.get('general.language.language', (value: string) => {
            switch (value) {
                case 'en-US':
                    this.locale = 'en';
                    break;
                default:
                    this.locale = value.toLowerCase();
                    break;
            }
        }, 'en');
    }

    /**
     * Set and transform the values of elements.
     *
     * @param {string} value
     */
    public setTransformedValue(value: string, event) {

        const selectionStart = event.target.selectionStart;
        const selectionEnd   = event.target.selectionEnd;

        if (this.isType(['number', 'currency'])) {
            this.setProcessableNumberValue(value);
            setTimeout(() => {
                event.target.setSelectionRange(selectionStart, selectionEnd);
            }, 5);
        }
    }

    /**
     * Set and transform the initial values of number elements.
     *
     * @param {string} value
     */
    protected setInitialNumberValues(value: string) {
        this.setReadableNumberValue(value);
    }

    protected setProcessableNumberValue(value: string) {
        this.val = this.toProcessableNumberValue(value);
    }

    protected setReadableNumberValue(value: string) {
        this.transformedValue = this.toReadableNumberValue(value);
    }

    protected toProcessableNumberValue(value: string) {
        numeral.locale(this.locale);

        return numeral(value.toString()).value();
    }

    protected toReadableNumberValue(value: string) {
        numeral.locale(this.locale);
        // make sure the value is a string
        value += '';
        switch (this.locale.toLowerCase()) {
            case 'nl-nl':
                if (value.indexOf('.') > -1) {
                    value = value.replace('.', ',');
                }
                break;
        }

        // Format depening on the given decimals. From http://numeraljs.com/#custom-formats,
        // should work with en, nl-nl
        const format = '0,0' + (this.decimals && this.decimals > 0 ? '.' + '0'.repeat(this.decimals) : '');

        return numeral(value).format(format);
    }

    protected setSettings() {
        this.setLocalizationSettings();

        if (this.isType(['number'])) {
            this.setNumberSettings();
        }
        if (this.isType(['currency'])) {
            this.setCurrencySettings();
        }
    }

    protected setLocalizationSettings() {
        this.settings.locale = this.locale || 'en';
    }

    protected setNumberLocalizationSettings() {
        if (this.settings.locale.toLowerCase() === 'en') {
            this.settings.number = Object.assign(this.settings.number, {
                decimalSymbol: '.',
                thousandsSeparatorSymbol: ',',
            });
        }

        if (this.settings.locale.toLowerCase() === 'nl-nl') {
            this.settings.number = Object.assign(this.settings.number, {
                decimalSymbol: ',',
                thousandsSeparatorSymbol: '.',
            });
        }
    }

    /**
     * Special onblur action for slug field
     */
    protected setSlugOnBlur() {
        this.inputElement.nativeElement.onblur = () => {

            this.val = this.val.toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^\w-]+/g, '');
        };
    }

    protected setNumberSettings() {
        this.settings.number = {
            prefix: '',
            allowDecimal: !!this.decimals,
            decimalLimit: this.decimals,
        };

        this.setNumberLocalizationSettings();

        if (this.applyNumberMask) {
            this.setNumberMask();
        }
    }

    protected setCurrencySettings() {
        this.settings.number = {
            prefix: '',
            allowDecimal: !!this.decimals,
            decimalLimit: this.decimals,
        };

        this.setNumberLocalizationSettings();

        this.setNumberMask();
    }

    protected setNumberMask() {
        this.masks.number = {
            placeholderChar: '\u0020',
            mask: (rawValue) => {

                let mask;
                if (this.settings.number.allowDecimal) {
                    mask = currencyMask(rawValue, this.settings.number);
                } else {
                    mask = createNumberMask(this.settings.number);
                    mask = mask(rawValue);
                }

                return mask;
            }
        };
    }

    /**
     * Returns the base currency object.
     *
     * @returns {CurrencyModel}
     */
    protected baseCurrency(): CurrencyModel {
        return this.currencies.find('code', this.currency);
    }

    protected toggleCheckbox() {
        if (!this.disabled && !this.readonly) {
            this.val = !this.val;
        }
    }

    protected toggleRadio() {
        if (!this.disabled && !this.readonly) {
            this.val = this.value;
        }
    }
}

function currencyMask(rawValue: string, settings) {
    const numberMask = createNumberMask(Object.assign(settings, {
        includeThousandsSeparator: true,
        allowDecimal: true,
        requireDecimal: true,
        allowLeadingZeroes: false,
        allowNegative: true
    }));
    const mask       = numberMask(rawValue);

    const decimalsRegex = /,([0-9]{1,2})/;
    const result        = decimalsRegex.exec(rawValue);

    // In case there is only one decimal
    if (result && result[1].length < 2) {
        mask.push('0');
    } else if (!result) {
        mask.push('0');
        mask.push('0');
    }

    return mask;
}


// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/components/input/input.component.ts